<script lang="ts">
  import { Link } from 'svelte-routing';
  import { api } from '../../api/client';
  import { activityIcon, clockTime } from '../../utils/activities';
  import { calendar, dayLetter, hours, shiftDay, today, weekRange, type LogWeek } from '../../utils/insights';
  import { locale, t } from '../../i18n/index.ts';

  // TrainingLog is a week of circles, one a day sized by its moving time with
  // a count when there was more than one activity (spec 006 FR-069); the
  // member steps back through weeks and taps a day for its activities
  // (006 FR-070). show opens a day's week with the day picked, as the year
  // calendar does (spec 006 FR-093); a new object each time reopens it.
  let { show = null }: { show?: { day: string } | null } = $props();
  let el: HTMLElement | undefined = $state();
  const MAX_R = 26;
  const MIN_R = 9;
  // A day of FULL_S moving time or more fills the largest circle.
  const FULL_S = 2 * 3600;

  let week = $state<LogWeek | null>(null);
  let current = $state<string | null>(null);
  let picked = $state<string | null>(null);
  let error = $state('');

  $effect(() => {
    const s = show;
    if (!s) return;
    load(s.day, s.day);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  load('');

  async function load(day: string, pick: string | null = null) {
    try {
      const got = await api.get<LogWeek>(`/me/log?${calendar()}${day ? `&week=${day}` : ''}`);
      week = got;
      current ??= got.week;
      picked = pick;
      error = '';
    } catch (e: any) {
      error = e.message;
    }
  }

  // radius grows with the circle's area, so twice the time looks twice as big.
  function radius(s: number): number {
    return MIN_R + (MAX_R - MIN_R) * Math.sqrt(Math.min(1, s / FULL_S));
  }

  let day = $derived(week?.days.find((d) => d.day === picked) ?? null);

  const dayFormat = new Intl.DateTimeFormat(locale(), { weekday: 'long', month: 'short', day: 'numeric' });

  // dayLabel is what a screen reader hears for a day's circle.
  function dayLabel(d: { day: string; moving_s: number }, n: number): string {
    const name = dayFormat.format(new Date(`${d.day}T12:00:00`));
    return n === 0 ? t('activitiesLogDayNone', { day: name }) : t('activitiesLogDay', { n, day: name, time: hours(d.moving_s) });
  }
</script>

<section bind:this={el} class="card p-4 space-y-3 scroll-mt-4" aria-labelledby="log-heading">
  <div class="flex items-center justify-between gap-2">
    <h2 id="log-heading" class="font-semibold">{t('activitiesLog')}</h2>
    {#if week}
      <div class="flex items-center gap-1 text-sm">
        <button class="btn btn-secondary btn-sm" aria-label={t('activitiesLogWeekBefore')} onclick={() => load(shiftDay(week!.week, -7))}><i aria-hidden="true" class="las la-chevron-left"></i></button>
        <span class="whitespace-nowrap px-1">{weekRange(week.week)}</span>
        <button class="btn btn-secondary btn-sm" aria-label={t('activitiesLogWeekAfter')} disabled={week.week === current} onclick={() => load(shiftDay(week!.week, 7))}><i aria-hidden="true" class="las la-chevron-right"></i></button>
      </div>
    {/if}
  </div>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if week}
    <ul class="grid grid-cols-7 gap-1">
      {#each week.days as d (d.day)}
        {@const n = d.activities.length}
        <li class="flex flex-col items-center gap-1">
          <button class="relative flex items-center justify-center rounded-full" style="width: {2 * MAX_R + 4}px; height: {2 * MAX_R + 4}px;"
            disabled={n === 0} aria-pressed={picked === d.day} onclick={() => (picked = picked === d.day ? null : d.day)}
            aria-label={dayLabel(d, n)}>
            {#if n > 0}
              <span class="rounded-full flex items-center justify-center text-[11px] font-medium"
                style="width: {2 * radius(d.moving_s)}px; height: {2 * radius(d.moving_s)}px; background: {picked === d.day ? 'var(--theme-accent)' : 'var(--theme-overlay1)'}; color: var(--theme-base);">
                {radius(d.moving_s) >= 16 ? hours(d.moving_s) : ''}
              </span>
              {#if n > 1}
                <span class="absolute top-0 right-0 rounded-full w-5 h-5 text-[11px] flex items-center justify-center" style="background: var(--theme-text); color: var(--theme-base);">{n}</span>
              {/if}
            {:else}
              <span class="w-1.5 h-1.5 rounded-full" style="background: var(--theme-surface2);"></span>
            {/if}
          </button>
          <span class="text-xs {d.day === today() ? 'font-bold' : 'text-text-muted'}" aria-hidden="true">{dayLetter(d.day)}</span>
          {#if d.day === today()}<span class="sr-only">{t('activitiesLogToday')}</span><span class="w-0 h-0 border-x-4 border-x-transparent border-b-4" style="border-bottom-color: var(--theme-text);" aria-hidden="true"></span>{/if}
        </li>
      {/each}
    </ul>
    {#if day}
      <ul class="space-y-1">
        {#each day.activities as a (a.id)}
          <li>
            <Link to="/activity/{a.id}" class="flex items-center gap-2 text-sm hover:opacity-80">
              <i aria-hidden="true" class="las {activityIcon(a.type)}"></i><span class="flex-1">{a.name}</span><span class="text-text-muted">{a.timed ? clockTime(a.moving_s) : ''}</span>
            </Link>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
</section>
